import React, { useId, useMemo, useState } from "react";
import styled from "styled-components";
import { LanguageNameSpaces, TimeRange } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { BASE_CHART_TIME_RANGES } from "@shared/common/constants";
import { getLocaleStringWithPrecision } from "@shared/utils/numbers";
import { TIME_RANGE_1_DAY } from "common/constants";
import {
  DASHBOARD_BALANCE_LOADING_ID,
  DASHBOARD_OVERVIEW_ID,
  DASHBOARD_PORTFOLIO_VALUE_ID,
} from "testIds";
import useMeasure from "utils/hooks/useMeasure";
import { monotonePath, Point } from "common/NewLook/monotonePath.ts";
import {
  NEW_LOOK_ON_BRAND,
  newLookBrandHero,
  newLookPhoneType,
} from "common/NewLook/tokens.ts";
import {
  NEW_LOOK_PHONE_GUTTER,
  NEW_LOOK_PHONE_HERO_MIN_FONT_SIZE,
} from "common/NewLook/layout.ts";
import { PhoneBrandLineChart } from "features/dashboard/PhoneDashboard/PhoneBrandCharts.tsx";
import { PhoneSkeleton } from "features/dashboard/PhoneDashboard/PhoneParts.tsx";
import {
  PhoneHeroCollapse,
  PhoneHeroRanges,
  PhoneHeroToggle,
} from "features/dashboard/PhoneDashboard/PhoneHeroControls.tsx";
import { shownAt, splitFraction } from "features/portfolio/NewLook/format.ts";
import useDashboardHistory from "features/dashboard/common/useDashboardHistory.ts";
import AutoSizeText from "common/Text/AutoSizeText.tsx";

interface PhoneHeroProps {
  selectedTimeRange: TimeRange;
  setSelectedTimeRange: (timeRange: TimeRange) => void;
  balanceLoading: boolean;
}

const CHART_HEIGHT = 110;
const CHART_H = CHART_HEIGHT;
const CHART_PAD_FULL = 8;
const SPARK_WIDTH = 96;
const SPARK_MIN_WIDTH = 56;
const SPARK_HEIGHT = 36;
const CHANGE_MIN_FONT_SIZE = 11;
const END_DOT_INSET = 6;
/** Remembers on this device whether the full chart is open. */
const OPEN_KEY = "phone_dashboard_chart_open";
const readOpen = () => {
  try {
    return localStorage.getItem(OPEN_KEY) === "true";
  } catch {
    return false;
  }
};

/**
 * The phone dashboard's blue header: what the portfolio is worth, how it moved
 * over the selected range, its history as a white area chart and the
 * history's time ranges.
 */
const PhoneHero = ({
  selectedTimeRange,
  setSelectedTimeRange,
  balanceLoading,
}: PhoneHeroProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);
  // Collapsed by default: the value, its move and a small sparkline; the
  // full chart and its ranges open on demand.
  const [open, setOpen] = useState(readOpen);
  const toggle = () =>
    setOpen((value) => {
      try {
        localStorage.setItem(OPEN_KEY, String(!value));
      } catch {
        // Storage can be unavailable (private mode); the toggle still works.
      }
      return !value;
    });
  // The live total, and its history for the range ending on it: the change
  // below is the history's, so it is what the chart and sparkline draw.
  const {
    total,
    totalLoading: loading,
    points,
    historyLoaded,
    change,
    changePercent,
    precision,
    isUsd,
    displayCurrency,
  } = useDashboardHistory(selectedTimeRange);
  const symbol = isUsd ? "$" : "₿";

  const values = useMemo(
    () => points.map((point) => ({ time: Number(point.rawX), value: point.y })),
    [points]
  );

  const [whole, fraction] = splitFraction(
    getLocaleStringWithPrecision(total, precision)
  );
  const shownChange = shownAt(change, precision);
  const sign = shownChange > 0 ? "+" : shownChange < 0 ? "−" : "";
  const arrow = shownChange < 0 ? "▼" : "▲";
  const amount = `${sign}${symbol}${getLocaleStringWithPrecision(
    Math.abs(change),
    precision
  )}`;
  const percent = `${Math.abs(changePercent).toFixed(2)}%`;

  const chartLabel = t("overview.chart_label", {
    range: selectedTimeRange.toUpperCase(),
  });
  const changeLoading = loading || (balanceLoading && !points.length);

  return (
    <Hero data-testid={DASHBOARD_OVERVIEW_ID} $open={open}>
      <Summary onClick={toggle}>
        <TopRow>
          <TopText>
            <Label>{t("overview.portfolio_value")}</Label>
            {loading ? (
              <span aria-busy="true" data-testid={DASHBOARD_BALANCE_LOADING_ID}>
                <PhoneSkeleton
                  $width={200}
                  $height={36}
                  style={{ margin: `4px ${NEW_LOOK_PHONE_GUTTER}px 0` }}
                />
              </span>
            ) : (
              <Total data-testid={DASHBOARD_PORTFOLIO_VALUE_ID}>
                {/* Holds the value's full width, so the sparkline gives way
                    to it instead of taking back what the shrunk text frees. */}
                <NaturalWidth aria-hidden={true}>
                  {symbol}
                  {whole}
                  {fraction}
                </NaturalWidth>
                <AutoSizeText
                  inheritFont={true}
                  minFontSizePx={NEW_LOOK_PHONE_HERO_MIN_FONT_SIZE}
                >
                  {symbol}
                  {whole}
                  {fraction && <Fraction>{fraction}</Fraction>}
                </AutoSizeText>
              </Total>
            )}
          </TopText>
          <Spark aria-hidden={open} $hidden={open}>
            <SparkChart>
              <HeroChart
                values={values}
                loading={false}
                label={chartLabel}
                height={SPARK_HEIGHT}
                fixedWidth={SPARK_WIDTH}
                compact={true}
              />
            </SparkChart>
          </Spark>
        </TopRow>
        <ChangeRow>
          {changeLoading ? (
            <PhoneSkeleton
              aria-busy="true"
              $width={180}
              $height={26}
              $radius={6}
              style={{ marginLeft: NEW_LOOK_PHONE_GUTTER }}
            />
          ) : (
            <Change>
              <AutoSizeText
                inheritFont={true}
                minFontSizePx={CHANGE_MIN_FONT_SIZE}
              >
                {arrow} {amount} ·{" "}
                {selectedTimeRange === TIME_RANGE_1_DAY
                  ? t("overview.today", { percent })
                  : t("overview.range_change", {
                      percent,
                      range: selectedTimeRange.toUpperCase(),
                    })}
              </AutoSizeText>
            </Change>
          )}
          <PhoneHeroToggle open={open} onToggle={toggle} />
        </ChangeRow>
      </Summary>
      <PhoneHeroCollapse open={open}>
        <PhoneBrandLineChart
          data={points}
          loading={balanceLoading && !points.length}
          displayCurrency={displayCurrency}
          precision={precision}
          isSuccess={historyLoaded}
        />
        <PhoneHeroRanges
          options={BASE_CHART_TIME_RANGES}
          value={selectedTimeRange}
          onChange={setSelectedTimeRange}
          label={t("overview.range_label")}
        />
      </PhoneHeroCollapse>
    </Hero>
  );
};

export default React.memo(PhoneHero);

const HeroChart = ({
  values,
  loading,
  label,
  height: CHART_HEIGHT = CHART_H,
  fixedWidth,
  compact,
}: {
  values: { time: number; value: number }[];
  loading: boolean;
  label: string;
  height?: number;
  fixedWidth?: number;
  compact?: boolean;
}) => {
  const [measure, { width: measured }] = useMeasure<HTMLDivElement>();
  const width = fixedWidth ?? measured;
  const CHART_PAD = compact ? 3 : CHART_PAD_FULL;
  const gradientId = `phone-hero-${useId().replace(/[^\w-]/g, "")}`;
  const shape = useMemo(() => {
    if (values.length < 2 || !width) return undefined;
    let min = Math.min(...values.map((p) => p.value));
    let max = Math.max(...values.map((p) => p.value));
    if (max === min) {
      const pad = Math.abs(max) * 0.01 || 1;
      min -= pad;
      max += pad;
    }
    const t0 = values[0].time;
    const t1 = values[values.length - 1].time;
    const coords: Point[] = values.map((p) => [
      t1 === t0 ? 0 : ((p.time - t0) / (t1 - t0)) * width,
      CHART_PAD +
        (1 - (p.value - min) / (max - min)) * (CHART_HEIGHT - CHART_PAD * 2),
    ]);
    const line = monotonePath(coords);
    const last = coords[coords.length - 1];
    return {
      line,
      area: `${line}L${last[0]},${CHART_HEIGHT}L0,${CHART_HEIGHT}Z`,
      last,
    };
  }, [values, width, CHART_HEIGHT, CHART_PAD]);

  return (
    <ChartWrap ref={measure} $height={CHART_HEIGHT} $compact={compact}>
      {loading && <PhoneSkeleton $width={9999} $height={60} $radius={0} />}
      {shape && (
        <svg
          width={width}
          height={CHART_HEIGHT}
          role="img"
          aria-label={label}
          style={{ display: "block" }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#fff" stopOpacity="0.3" />
              <stop offset="1" stopColor="#fff" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={shape.area} fill={`url(#${gradientId})`} />
          <path
            d={shape.line}
            fill="none"
            stroke="#fff"
            strokeWidth={2}
            strokeLinejoin="round"
          />
          {!compact && (
            <circle
              cx={shape.last[0] - END_DOT_INSET}
              cy={shape.last[1]}
              r={4}
              fill="#fff"
            />
          )}
        </svg>
      )}
    </ChartWrap>
  );
};

const Summary = styled.div`
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
`;
const Hero = styled.div<{ $open: boolean }>`
  margin: 0 ${NEW_LOOK_PHONE_GUTTER}px;
  padding: 16px 0 12px;
  border-radius: ${(p) => p.theme.radii.card}px;
  overflow: hidden;
  ${newLookBrandHero};
  ${PhoneSkeleton} {
    background: ${NEW_LOOK_ON_BRAND.well};
  }
`;
const ChartWrap = styled.div<{ $height: number; $compact?: boolean }>`
  min-height: ${(p) => p.$height}px;
  margin-top: ${(p) => (p.$compact ? 0 : 8)}px;
`;
const TopRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  padding-right: ${NEW_LOOK_PHONE_GUTTER}px;
`;
const TopText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;
const Spark = styled.div<{ $hidden: boolean }>`
  flex: 0 100 ${SPARK_WIDTH}px;
  min-width: 0;
  overflow: hidden;
  width: ${SPARK_WIDTH}px;
  container-type: inline-size;
  /* The total has priority; the sparkline goes on the narrowest phones. */
  @media (max-width: 359px) {
    display: none;
  }
  margin-bottom: 6px;
  transition: opacity 0.2s ease;
  opacity: ${(p) => (p.$hidden ? 0 : 1)};
`;
const SparkChart = styled.div`
  /* A long total squeezes the sparkline; a sliver of it reads as noise. */
  @container (max-width: ${SPARK_MIN_WIDTH - 0.02}px) {
    visibility: hidden;
  }
`;
const ChangeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
  padding-right: ${NEW_LOOK_PHONE_GUTTER - 8}px;
`;
const Label = styled.div`
  padding: 0 ${NEW_LOOK_PHONE_GUTTER}px;
  ${newLookPhoneType.label};
  color: ${NEW_LOOK_ON_BRAND.secondary};
`;
const Total = styled.div`
  padding: 0 ${NEW_LOOK_PHONE_GUTTER}px;
  margin-top: 4px;
  /* One line always: it scales down on narrow phones instead of wrapping. */
  ${newLookPhoneType.hero};
  font-weight: 500;
  white-space: nowrap;
`;
const NaturalWidth = styled.div`
  height: 0;
  overflow: hidden;
  visibility: hidden;
`;
const Fraction = styled.span`
  color: rgba(255, 255, 255, 0.62);
`;
const Change = styled.div`
  min-width: 0;
  margin-left: ${NEW_LOOK_PHONE_GUTTER}px;
  padding: 4px 10px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.18);
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
`;
